import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router';
import type { ChangeEvent } from 'react';
import { Title } from '../../components/Title';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Container,
    Divider,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useMediaQuery,
    Theme,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    Checkbox,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import type { ResellerCheckoutInfo } from '../../types/reseller';
import { accountService, UserRole } from '../../account.service';
import { resellerService } from '../../reseller.service';
import { useMounted } from '../../hooks/use-mounted';
import { Scrollbar } from '../../components/Scrollbar';
import { PropertyList } from '../../components/PropertyList';
import { PropertyListItem } from '../../components/PropertyListItem';
import numeral from 'numeral';
import CreditCard from 'react-credit-cards';
import { formatCreditCardNumber, formatExpirationDate, formatCVC } from '../../utils/credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import creditCards from '../../images/credit-cards.png';
import { IToastState, defaultToastState } from '../../components/ToastState';
import type { Country } from '../../types/helper';
import { usaStatesAndTerritories } from '../../utils/cart';
import { canadaProvinces } from '../../utils/cart';
import { CartCheckoutRequest, CheckoutCreateRequest } from '../../cart.service';
import { settings } from '../../settings';

const tabs = [
    { label: 'Shipping', value: 'shipping' },
    { label: 'Billing', value: 'billing' },
];

const translateCardType = (issuer: string): string => {
    switch (issuer) {
        case 'visa':
            return 'VISA';
        case 'mastercard':
            return 'MC';
        case 'amex':
            return 'AMEX';
        case 'dinersclub':
            return 'DINE';
        case 'discover':
            return 'DISC';
        default:
            return '';
    }
};

const ANNUAL_SUPPORT_ID = 36;

interface ShippingData {
    firstName: string;
    lastName: string;
    company: string;
    address: string;
    city: string;
    zip: string;
    state: string;
    phone: string;
    email: string;
    po: string;
    memo: string;
}
function emptyShipping(): ShippingData {
    return {
        firstName: '',
        lastName: '',
        company: '',
        address: '',
        city: '',
        zip: '',
        state: '',
        phone: '',
        email: '',
        po: '',
        memo: '',
    };
}

export const ResellerCheckout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const account = accountService.getAccount();
    const { cart, subtotal, original_order } = location.state || {};

    // determine if the cart contains only the annual support product
    let isAnnualSupport = cart.every((element) => element.productId === ANNUAL_SUPPORT_ID);

    const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const align = smDown ? 'vertical' : 'horizontal';

    const isMounted = useMounted();
    const [currentTab, setCurrentTab] = useState<string>('shipping');
    const [info, setInfo] = useState<ResellerCheckoutInfo | null>(null);
    const [shipping] = useState<ShippingData | null>(emptyShipping());

    const [countries, setCountries] = useState<Country[]>([]);
    const [country, setCountry] = useState<string>('');
    const [canadaProvince, setCanadaProvince] = useState<number>(0);
    const [usaState, setUsaState] = useState<number>(0);

    const [openDialog, setOpenDialog] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    // Set when the API blocks a create-order as a recent duplicate (409). Drives
    // the "View your order" link in the failure dialog (-> /invoices/{id}).
    const [duplicateOrderId, setDuplicateOrderId] = useState('');
    const [openMonerisDialog, setOpenMonerisDialog] = useState(false);
    const [toastState, setToastState] = useState<IToastState>(defaultToastState);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [invoiceDisabled, setInvoiceDisabled] = useState(false);
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);

    const { handleSubmit, control } = useForm<ShippingData>();

    const [cardNumber, setCardNumber] = useState<string>('');
    const [expiry, setExpiry] = useState<string>('');
    const [cvc, setCvc] = useState<string>('');
    const [focused, setFocused] = useState<string>('');
    const [issuer, setIssuer] = useState<string>('');
    const [inProgress, setInProgress] = useState<boolean>(false);
    const inProgressRef = useRef(false);

    const loadData = useCallback(async () => {
        try {
            const resellerId = accountService.getAccount().id;
            const data = await resellerService.getCheckoutInfo(resellerId, subtotal / 100);
            const fetchedCountries = await resellerService.getCountries();
            if (isMounted()) {
                setInfo(data);
                setCountries(fetchedCountries);
                setCountry('US');
            }
        } catch (err) {
            console.error(err);
        }
    }, [isMounted]);

    useEffect(() => {
        if (account) {
            loadData();
        }
    }, []);

    const handleTabsChange = (event: ChangeEvent<{}>, value: string): void => {
        setCurrentTab(value);
    };

    const handleCallback = ({ issuer }, isValid) => {
        if (isValid) {
            setIssuer(issuer);
        } else {
            setIssuer('');
        }
    };

    const handleInputFocus = ({ target }) => {
        setFocused(target.name);
    };

    const handleInputChange = ({ target }) => {
        if (target.name === 'number') {
            target.value = formatCreditCardNumber(target.value);
            setCardNumber(target.value);
        } else if (target.name === 'expiry') {
            target.value = formatExpirationDate(expiry, target.value);
            setExpiry(target.value);
        } else if (target.name === 'cvc') {
            target.value = formatCVC(target.value, cardNumber);
            setCvc(target.value);
        }
    };

    const onSubmit = async (data: ShippingData, isInvoice: boolean) => {
        if (inProgressRef.current) return;
        inProgressRef.current = true;
        setInProgress(true);

        try {
            // Create server-side checkout session with validated prices
            const billingState =
                country === 'CA'
                    ? canadaProvinces[canadaProvince].short
                    : country === 'US'
                    ? usaStatesAndTerritories[usaState].short
                    : data.state;
            // Billing comes from the reseller profile (read-only Billing tab),
            // shipping from the form (Shipping tab). Server applies the
            // per-line reseller discount from ResellerService.GetProducts —
            // no DiscountCode needed on this path.
            const checkoutRequest: CheckoutCreateRequest = {
                type: isInvoice ? 'INV' : translateCardType(issuer),
                itemsJson: JSON.stringify(
                    cart.map((item) => ({
                        pid: item.productId,
                        lic: item.licenseId,
                        qty: item.qty,
                    }))
                ),
                billingJson: JSON.stringify({
                    firstName: info?.firstName ?? '',
                    lastName: info?.lastName ?? '',
                    company: info?.company ?? '',
                    address: info?.address ?? '',
                    city: info?.city ?? '',
                    state: info?.state ?? '',
                    country: info?.countryCode ?? '',
                    zip: info?.zip ?? '',
                    email: info?.email ?? '',
                    phone: info?.phone ?? '',
                }),
                shippingJson: JSON.stringify({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    company: data.company,
                    address: data.address,
                    city: data.city,
                    state: billingState,
                    country: country,
                    zip: data.zip,
                    email: data.email,
                    phone: data.phone,
                }),
                comments: isAnnualSupport ? data.memo : '',
            };
            const checkoutResult = await resellerService.createCheckout(checkoutRequest);
            if (!checkoutResult.ok || !checkoutResult.body) {
                setFailMessage(checkoutResult.message || 'Failed to create checkout session');
                // Duplicate-order 409: offer a link to the order already placed.
                setDuplicateOrderId(
                    checkoutResult.error?.status === 'duplicate_recent_order'
                        ? checkoutResult.error.existingOrderId ?? ''
                        : ''
                );
                setOpenDialog(true);
                setInProgress(false);
                return;
            }
            const checkoutSession = checkoutResult.body;

            let request: CartCheckoutRequest = {
                reseller_id: accountService.getAccount().id,
                bill_first_name: data.firstName,
                bill_last_name: data.lastName,
                bill_company: data.company,
                bill_address_one: data.address,
                bill_city: data.city,
                bill_state_or_province:
                    country === 'CA'
                        ? canadaProvinces[canadaProvince].long
                        : country === 'US'
                        ? usaStatesAndTerritories[usaState].long
                        : data.state,
                bill_country: country,
                bill_postal_code: data.zip,
                bill_email: data.email,
                bill_phone: data.phone,
                bill_fax: data.memo || '',
                bill_po: data.po || '',

                credit_card_brand: '',
                credit_card_number: '',
                expire_month: '',
                expire_year: '',
                cvv: '',
            };
            request['checkout_token'] = checkoutSession.token;
            if (isInvoice) {
                request['order_type'] = 'INV';
            } else {
                request['credit_card_brand'] = translateCardType(issuer);
                request['credit_card_number'] = cardNumber.replaceAll(' ', '');
                request['expire_month'] = expiry.substr(0, 2);
                request['expire_year'] = '20' + expiry.substr(3, 2);
                request['cvv'] = cvc;
            }

            cart.forEach((item, idx) => {
                request[`product_id${idx > 0 ? idx : ''}`] = item.productId;
                request[`product${idx > 0 ? idx : ''}`] = item.productName;
                request[`license${idx > 0 ? idx : ''}`] = item.licenseId;
                request[`quantity${idx > 0 ? idx : ''}`] = item.qty;
            });

            const response = await resellerService.checkout(request);

            if (response == null) {
                setFailMessage('Server communication error');
                setOpenDialog(true);
                setInProgress(false);
            } else if (!response.status) {
                if (response.message === 'moneris problem') {
                    setOpenMonerisDialog(true);
                    setInProgress(false);
                } else {
                    setFailMessage(response.message);
                    setOpenDialog(true);
                    setInProgress(false);
                }
            } else {
                const toast_message = isInvoice
                    ? 'Invoice was successfully created'
                    : 'Payment was successfully processed';
                setToastState({ open: true, status: 'success', message: toast_message });
                setIssuer('');
                setCardNumber('');
                setExpiry('');
                setCvc('');
                setInProgress(false);
                navigate(`/invoices/${response.order}`);
            }
        } finally {
            inProgressRef.current = false;
        }
    };

    const onInvoice = () => {
        setAcceptedTerms(false);
        setInvoiceDisabled(true);
        setOpenInvoiceDialog(true);
    };

    const onSubmitCreditCard = async (data: ShippingData) => {
        onSubmit(data, false);
    };
    const onSubmitInvoice = async (data: ShippingData) => {
        setOpenInvoiceDialog(false);
        onSubmit(data, true);
    };

    const handleCancelInvoiceDialog = () => {
        setOpenInvoiceDialog(false);
    };

    const handleCloseMonerisDialog = () => {};
    const handleCancelMonerisDialog = () => {
        setOpenMonerisDialog(false);
    };

    const handleAcceptTerms = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setAcceptedTerms(true);
            setInvoiceDisabled(false);
        } else {
            setAcceptedTerms(false);
            setInvoiceDisabled(true);
        }
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    const onCountryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCountry = event.target.value;
        setCountry(newCountry);
    };

    const onProvinceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newProv = event.target.value;
        setCanadaProvince(Number.parseInt(newProv));
    };

    const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsaState(Number.parseInt(event.target.value));
    };

    return (
        <>
            {(!account || account.role !== UserRole.Reseller) && <Navigate to="/account/login" />}
            <Title title="Checkout" />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 3,
                }}
            >
                {account && (
                    <Container maxWidth="lg">
                        <Dialog
                            open={openDialog}
                            onClose={() => {
                                setOpenDialog(false);
                                setDuplicateOrderId('');
                            }}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">Transaction Failed</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">{failMessage}</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                {duplicateOrderId && (
                                    <Button
                                        onClick={() => {
                                            setOpenDialog(false);
                                            setDuplicateOrderId('');
                                            navigate(`/invoices/${duplicateOrderId}`);
                                        }}
                                    >
                                        View your order
                                    </Button>
                                )}
                                <Button
                                    onClick={() => {
                                        setOpenDialog(false);
                                        setDuplicateOrderId('');
                                    }}
                                    autoFocus
                                >
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openInvoiceDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">Software delivery terms</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Software download will only be available after invoice has been paid.
                                </DialogContentText>
                                <Box sx={{ mt: 2 }}>
                                    <Checkbox
                                        checked={acceptedTerms}
                                        value={acceptedTerms}
                                        color="primary"
                                        onChange={handleAcceptTerms}
                                    />
                                    I agree to software delivery terms
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button sx={{ mr: 2 }} onClick={handleCancelInvoiceDialog} autoFocus>
                                    Cancel
                                </Button>
                                <form id="payment" onSubmit={handleSubmit(onSubmitInvoice)}>
                                    <Button
                                        sx={{ mr: 2 }}
                                        color="primary"
                                        variant="contained"
                                        type="submit"
                                        disabled={invoiceDisabled}
                                    >
                                        OK
                                    </Button>
                                </form>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openMonerisDialog}
                            onClose={handleCloseMonerisDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">Payment processing failed</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    We experience payment processing issues with our provider. You can try again to
                                    process payment later on, or use PayPal as a credit card payment processor.
                                </DialogContentText>
                                <DialogContentText sx={{ mt: 2 }}>
                                    If you wish to use PayPal, you need to create an invoice first. You can use PayPal
                                    payment method once invoice is created.
                                </DialogContentText>
                                <DialogContentText sx={{ mt: 2 }}>
                                    You can also contact our sales department {settings.phone1} for order processing
                                    over the phone.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button sx={{ mr: 2 }} onClick={handleCancelMonerisDialog} autoFocus>
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Snackbar
                            open={toastState.open}
                            autoHideDuration={6000}
                            onClose={handleCloseSnackbar}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        >
                            <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                                {toastState.message}
                            </Alert>
                        </Snackbar>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Card>
                                    <CardHeader title="Order Summary" />
                                    <CardContent sx={{ py: 0 }}>
                                        <Scrollbar>
                                            <Box sx={{ minWidth: 700 }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Product</TableCell>
                                                            <TableCell>License</TableCell>
                                                            <TableCell align="right">Price</TableCell>
                                                            <TableCell align="right">Qty</TableCell>
                                                            <TableCell align="right">Amount</TableCell>
                                                            <TableCell align="right">Discount</TableCell>
                                                            <TableCell align="right">Total</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {cart.map((product, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{product.productName}</TableCell>
                                                                <TableCell>{product.licenseName}</TableCell>
                                                                <TableCell align="right">
                                                                    ${numeral(product.price / 100).format('0,0.00')}
                                                                </TableCell>
                                                                <TableCell align="right">{product.qty}</TableCell>
                                                                <TableCell align="right">
                                                                    ${numeral(product.amount / 100).format('0,0.00')}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    ${numeral(product.discount / 100).format('0,0.00')}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {' '}
                                                                    ${numeral(product.total / 100).format('0,0.00')}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {info && info.taxes && (
                                                            <TableRow>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        Subtotal
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        ${numeral(subtotal / 100).format('0,0.00')}{' '}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}

                                                        {info &&
                                                            info.taxes &&
                                                            info.taxes.map((tax, index) => {
                                                                return (
                                                                    <TableRow key={index}>
                                                                        <TableCell />
                                                                        <TableCell />
                                                                        <TableCell />
                                                                        <TableCell />
                                                                        <TableCell />
                                                                        <TableCell align="right">{tax.name}</TableCell>
                                                                        <TableCell align="right">
                                                                            ${numeral(tax.amount).format('0,0.00')}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}

                                                        {info && (
                                                            <TableRow>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        Order Total
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography style={{ fontWeight: 600 }}>
                                                                        US ${numeral(info.total).format('0,0.00')}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Scrollbar>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Tabs
                                    indicatorColor="primary"
                                    onChange={handleTabsChange}
                                    scrollButtons="auto"
                                    textColor="primary"
                                    value={currentTab}
                                    variant="scrollable"
                                >
                                    {tabs.map((tab) => (
                                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                                    ))}
                                </Tabs>
                                <Divider sx={{ mb: 3 }} />
                                {currentTab === 'shipping' && (
                                    <Card sx={{ p: 3 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="firstName"
                                                control={control}
                                                defaultValue={shipping.firstName}
                                                rules={{
                                                    required: 'First Name is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'First Name cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        required
                                                        label="First Name"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="lastName"
                                                control={control}
                                                defaultValue={shipping.lastName}
                                                rules={{
                                                    required: 'Last Name is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Last Name cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        required
                                                        label="Last Name"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="company"
                                                control={control}
                                                defaultValue={shipping.company}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Company name cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Company"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="address"
                                                control={control}
                                                defaultValue={shipping.address}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Address cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Address"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="city"
                                                control={control}
                                                defaultValue={shipping.city}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'City name cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="City"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="zip"
                                                control={control}
                                                defaultValue={shipping.zip}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Zip/postal code cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Zip / Postal Code"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        {countries.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    label="Country"
                                                    size="small"
                                                    select
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                    value={country}
                                                    onChange={onCountryChange}
                                                >
                                                    {countries.map((country, idx) => (
                                                        <MenuItem key={idx} value={country.code}>
                                                            {country.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        )}

                                        {country === 'US' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    label="State"
                                                    size="small"
                                                    select
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                    value={usaState}
                                                    onChange={onStateChange}
                                                >
                                                    {usaStatesAndTerritories.map((state, idx) => (
                                                        <MenuItem key={idx} value={idx}>
                                                            {state.long}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        )}

                                        {country === 'CA' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextField
                                                    label="Province"
                                                    size="small"
                                                    select
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                    value={canadaProvince}
                                                    onChange={onProvinceChange}
                                                >
                                                    {canadaProvinces.map((prov, idx) => (
                                                        <MenuItem key={idx} value={idx}>
                                                            {prov.long}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        )}

                                        {country !== 'CA' && country !== 'US' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    mt: 2,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Controller
                                                    name="state"
                                                    control={control}
                                                    defaultValue={shipping.state}
                                                    rules={{
                                                        maxLength: {
                                                            value: 150,
                                                            message:
                                                                'State / Province / Region cannot exceed 150 characters',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label="State / Province / Region"
                                                            size="small"
                                                            sx={{
                                                                flexGrow: 1,
                                                            }}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="phone"
                                                control={control}
                                                defaultValue={shipping.phone}
                                                rules={{
                                                    required: 'Phone is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Phone cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Phone"
                                                        size="small"
                                                        required
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="email"
                                                control={control}
                                                defaultValue={shipping.email}
                                                rules={{
                                                    required: 'Email is required',
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'Email cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Email"
                                                        size="small"
                                                        required
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="po"
                                                control={control}
                                                defaultValue={shipping.po}
                                                rules={{
                                                    maxLength: {
                                                        value: 150,
                                                        message: 'PO# cannot exceed 150 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label="PO #"
                                                        size="small"
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mt: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Controller
                                                name="memo"
                                                control={control}
                                                defaultValue={original_order}
                                                rules={{
                                                    required: `${
                                                        isAnnualSupport ? 'Original Order ID is required' : ''
                                                    }`,
                                                    maxLength: {
                                                        value: 150,
                                                        message: `${
                                                            isAnnualSupport ? 'Original Order ID' : 'Memo'
                                                        } cannot exceed 150 characters`,
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        label={isAnnualSupport ? 'Original Order ID' : 'Memo'}
                                                        size="small"
                                                        required={isAnnualSupport}
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Card>
                                )}
                                {currentTab === 'billing' && (
                                    <Box>
                                        <Card>
                                            <Box sx={{ px: 3 }}>
                                                <PropertyList>
                                                    <PropertyListItem align={align} label="Name">
                                                        <Typography color="primary" variant="body1">
                                                            {info && `${info.firstName} ${info.lastName}`}
                                                        </Typography>
                                                    </PropertyListItem>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Company">
                                                            <Typography variant="body1">
                                                                {info && info.company}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Address">
                                                            <Typography variant="body1">
                                                                {info && info.address}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="City">
                                                            <Typography variant="body1">{info && info.city}</Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="State / Province">
                                                            <Typography variant="body1">
                                                                {info && info.state}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Zip / Postal code">
                                                            <Typography variant="body1">{info && info.zip}</Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Country">
                                                            <Typography variant="body1">
                                                                {info && info.country}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Phone">
                                                            <Typography variant="body1">
                                                                {info && info.phone}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Fax">
                                                            <Typography variant="body1">{info && info.fax}</Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Email">
                                                            <Typography variant="body1" color="primary">
                                                                {info && info.email}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                </PropertyList>
                                            </Box>
                                        </Card>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Box sx={{ height: '100%' }}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ px: 3 }}>
                                            <Box
                                                sx={{
                                                    mt: 0,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Typography variant="h6">Pay with &nbsp;</Typography>
                                                        <img
                                                            alt="credit cards"
                                                            style={{ width: 340 }}
                                                            src={creditCards}
                                                        />
                                                    </Box>

                                                    <CreditCard
                                                        acceptedCards={[
                                                            'visa',
                                                            'mastercard',
                                                            'amex',
                                                            'dinersclub',
                                                            'discover',
                                                        ]}
                                                        number={cardNumber}
                                                        name=""
                                                        expiry={expiry}
                                                        cvc={cvc}
                                                        focused={focused}
                                                        callback={handleCallback}
                                                    />
                                                </Box>
                                                <Box>
                                                    <Grid container columnSpacing={3}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                name="number"
                                                                value={cardNumber}
                                                                required
                                                                label="Card Number"
                                                                fullWidth
                                                                onChange={handleInputChange}
                                                                onFocus={handleInputFocus}
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    mr: 3,
                                                                    mt: 3,
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item md={6} xs={12}>
                                                            <TextField
                                                                name="expiry"
                                                                value={expiry}
                                                                required
                                                                label="Valid Thru (MM/YY)"
                                                                fullWidth
                                                                onChange={handleInputChange}
                                                                onFocus={handleInputFocus}
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    mr: 3,
                                                                    mt: 3,
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item md={6} xs={12}>
                                                            <TextField
                                                                name="cvc"
                                                                value={cvc}
                                                                label="CVC"
                                                                fullWidth
                                                                onChange={handleInputChange}
                                                                onFocus={handleInputFocus}
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    mr: 3,
                                                                    mt: 3,
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <form
                                                                id="payment"
                                                                onSubmit={handleSubmit(onSubmitCreditCard)}
                                                            >
                                                                <Button
                                                                    color="primary"
                                                                    variant="contained"
                                                                    type="submit"
                                                                    fullWidth
                                                                    disabled={
                                                                        !issuer || expiry.length !== 5 || inProgress
                                                                    }
                                                                    sx={{
                                                                        mt: 3,
                                                                        height: 48,
                                                                        fontSize: '1rem',
                                                                        fontWeight: 700,
                                                                        textTransform: 'none',
                                                                    }}
                                                                >
                                                                    PAY with Card
                                                                </Button>
                                                            </form>

                                                            <Divider sx={{ m: 2 }} variant="middle">
                                                                OR
                                                            </Divider>
                                                            <form id="invoice" onSubmit={handleSubmit(onInvoice)}>
                                                                <Button
                                                                    color="primary"
                                                                    variant="contained"
                                                                    type="submit"
                                                                    fullWidth
                                                                    disabled={inProgress}
                                                                    sx={{
                                                                        height: 48,
                                                                        fontSize: '1rem',
                                                                        fontWeight: 700,
                                                                        textTransform: 'none',
                                                                    }}
                                                                >
                                                                    INVOICE (to pay later)
                                                                </Button>
                                                            </form>

                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: '#666', fontStyle: 'italic' }}
                                                                >
                                                                    You can use PayPal payment method once invoice is
                                                                    created
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sx={{}}>
                                <Divider />
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Typography variant="subtitle2">
                                        Secure payment services provided by{' '}
                                        <Box component="span" fontWeight="fontWeightBold">
                                            Royal Bank of Canada
                                        </Box>{' '}
                                        and{' '}
                                        <Box component="span" fontWeight="fontWeightBold">
                                            LSoft Technologies
                                        </Box>
                                    </Typography>
                                </Box>
                                {info && (
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Typography variant="subtitle2" fontSize="80%" sx={{ ml: 1 }}>
                                            You will be charged CAD {numeral(info.total * info.exRate).format('0,0.00')}{' '}
                                            at rate {info.exRate}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Container>
                )}
            </Box>
        </>
    );
};
